import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { customAuth, headerAuth } from "../standardAuthPatterns";
import {
  defineProduct,
  productVariant,
  type ConnectorDefinition,
} from "../types";
import logo from "./logo.svg";
import { anthropicMessagesUsage } from "./usage";
import { anthropicClientUsageHint } from "./usageHint";

const DEFAULT_ANTHROPIC_BASE_URL = "https://api.anthropic.com/v1";

const anthropicModelsFetch = openAiModelsFetch(
  "Fetch models from Anthropic",
  // The default page size is 20 and extract reads a single response, so
  // anything past the first page would be dropped.
  ({ baseUrl }) =>
    baseUrlEndpoint(baseUrl, DEFAULT_ANTHROPIC_BASE_URL, "/models?limit=100")
);

const modelsStep = {
  title: "Choose available models (optional)",
  items: [
    {
      kind: "list",
      label: "Available models",
      prefix: MODELS_PREFIX,
      columns: [{ field: "name", placeholder: "claude-opus-4-8" }],
      addLabel: "Add model",
      fetch: anthropicModelsFetch,
      helperText: MODELS_HELPER_TEXT,
    },
  ],
} satisfies ConnectorDefinition["steps"][number];

const apiKeyVariant = productVariant({
  identifier: "anthropic",
  authLabel: "API key",
  protocol: "http",
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_ANTHROPIC_BASE_URL),
  auth: (app) =>
    headerAuth([
      {
        name: "anthropic-version",
        value: "2023-06-01",
      },
      { name: "x-api-key", value: app.secret.apiKey },
    ]),
  steps: [
    {
      title: "Get an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Anthropic Console](https://console.anthropic.com/settings/keys) and create or copy an API key.",
        },
      ],
    },
    {
      title: "Copy and paste the API key into the field below",
      items: [
        {
          kind: "input",
          label: "Anthropic API key",
          placeholder: "sk-ant-...",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "baseUrl",
          label: "Base URL (optional)",
          placeholder: DEFAULT_ANTHROPIC_BASE_URL,
          defaultValue: DEFAULT_ANTHROPIC_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default Anthropic API endpoint.",
        },
      ],
    },
    modelsStep,
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_ANTHROPIC_BASE_URL, "/models"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    ANTHROPIC_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_ANTHROPIC_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(anthropicClientUsageHint, configuration),
  usage: anthropicMessagesUsage(),
});

const federationVariant = productVariant({
  identifier: "anthropicFederation",
  authLabel: "Workload identity federation (keyless)",
  protocol: "http",
  defaultUrlPattern: () =>
    baseUrlPattern(undefined, DEFAULT_ANTHROPIC_BASE_URL),
  auth: (app) =>
    customAuth("anthropicFederation", {
      issuer: app.issuer ?? "",
      federationRuleId: app.config.federationRuleId,
      organizationId: app.config.organizationId,
      serviceAccountId: app.config.serviceAccountId,
      workspaceId: app.config.workspaceId,
    }),
  steps: [
    {
      title: "Configure workload identity federation",
      items: [
        {
          kind: "markdown",
          content:
            "In the [Claude Console](https://platform.claude.com/settings/workload-identity-federation), select **Connect workload** and choose **Custom OIDC**. Register the issuer below using OIDC discovery. The issuer must be publicly reachable over HTTPS.",
        },
        {
          kind: "copyable",
          label: "Issuer URL",
          value: "{OIDC_PROVIDER_URL}",
        },
        {
          kind: "markdown",
          content:
            "Set both the rule’s **Subject prefix** (an exact match, without a trailing wildcard) and **Audience** to the value below. Select a service account with membership in the workspace you want to use, and choose **workspace:inference** for model inference or **workspace:developer** if you also need Files or Skills access.",
        },
        {
          kind: "copyable",
          label: "Subject and audience",
          value: "connected-app:{CONNECTED_APP_ID}",
        },
      ],
    },
    {
      title: "Enter your federation details",
      items: [
        {
          kind: "input",
          label: "Federation rule ID",
          placeholder: "fdrl_…",
          required: true,
          target: { kind: "configuration", field: "federationRuleId" },
        },
        {
          kind: "input",
          label: "Organization ID",
          required: true,
          helperText:
            "Copy the organization UUID from the Claude Console’s Organization settings.",
          target: { kind: "configuration", field: "organizationId" },
        },
        {
          kind: "input",
          label: "Service account ID",
          placeholder: "svac_…",
          required: true,
          target: { kind: "configuration", field: "serviceAccountId" },
        },
        {
          kind: "input",
          label: "Workspace ID (optional)",
          placeholder: "wrkspc_…",
          helperText:
            "Required when the federation rule covers more than one workspace. Otherwise, Anthropic uses the rule’s workspace.",
          target: { kind: "configuration", field: "workspaceId" },
        },
      ],
    },
    modelsStep,
  ],
  connectionTest: {
    protocol: "http",
    request: () => `${DEFAULT_ANTHROPIC_BASE_URL}/models`,
  },
  publicEnvVars: () => ({ ANTHROPIC_BASE_URL: DEFAULT_ANTHROPIC_BASE_URL }),
  usageHint: (configuration) =>
    withAllowedModelsHint(anthropicClientUsageHint, configuration),
  usage: anthropicMessagesUsage(),
});

export const anthropic = defineProduct({
  name: "Anthropic",
  tagLine: "AI models and Claude",
  keywords:
    "claude opus sonnet haiku llm models inference messages oidc federation",
  logo,
  tags: ["featured", "ai", "engineering"],
  variants: [apiKeyVariant, federationVariant],
});
